import { Box, Button, Dialog, DialogContent, DialogTitle, Grid, IconButton, Input, Stack } from '@mui/material';
import axios, { AxiosError } from 'axios';
import {
	AlignmentButtons,
	ClearButtons,
	ColorButtons,
	FormatButtons,
	ImageButtons,
	LevelSelect,
	LinkButtons,
	ListButtons,
	QuoteButtons,
	RichTextInput,
	RichTextInputToolbar,
} from 'ra-input-rich-text';
import { useCallback, useEffect, useState } from 'react';
import {
	BooleanInput,
	Datagrid,
	Edit,
	FormTab,
	FunctionField,
	NumberInput,
	ReferenceManyField,
	SaveButton,
	TabbedForm,
	TextField,
	Toolbar,
	useEditController,
	useNotify,
	usePermissions,
} from 'react-admin';
import { BiTrash } from 'react-icons/bi';
import constants from '../../constants';
import { getLocalizedErrorMessage } from '../../utils/functions';
import MyPagination from '../MyPagination';
import AddDomainModal from './AddDomainModal';
import AuthOptions from './AuthOptions';
import DeleteCompanyDialog from './DeleteComapnyDialog';
import ExecutiveSummaryGenerator from './ExecutiveSummaryGenerator';
import FactorsModal from './FactorsModal';
import GeneralCompanyInfo from './GeneralCompanyInfo';
import CompanyBannerTab from './CompanyBannerTab';
import HREdit from './HR/HREdit';
import MiluimForm from './Miluim/MiluimForm';
import Departments from './Departments';
import Statistics from './Statistics';
import LoginHistory from './LoginHistory';
import SubsidyManagement from './SubsidyManagement';

const WIZMANN_COMPANY_ID = '67568ada04e3a4ad8bd53afe';

const WizmannTypeStats = ({ companyId }: { companyId: string }) => {
	const [data, setData] = useState<Record<string, number> | null>(null);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const notify = useNotify();

	useEffect(() => {
		const fetchData = async () => {
			try {
				setLoading(true);
				setError(null);
				const res = await axios.get(`${constants.API_URL}/member/wizmann-type-counts`, {
					params: { company_id: companyId },
				});
				setData(res.data);
			} catch (err) {
				setError('אירעה שגיאה בטעינת הסטטיסטיקות');
				notify(getLocalizedErrorMessage(err as AxiosError), { type: 'error' });
			} finally {
				setLoading(false);
			}
		};

		if (companyId) {
			fetchData();
		}
	}, [companyId, notify]);

	if (loading) {
		return <div>טוען סטטיסטיקות...</div>;
	}

	if (error) {
		return <div>{error}</div>;
	}

	if (!data) {
		return null;
	}

	return (
		<Stack spacing={2}>
			<h3>סטטיסטיקת wizmann_type</h3>
			<Grid container spacing={2}>
				{Object.entries(data)
					.filter(([label]) => label !== 'total' && label !== 'ללא_סוג')
					.map(([label, value]) => (
					<Grid item xs={12} sm={6} md={3} key={label}>
						<Box borderRadius={2} padding={2}>
							<div className="text-sm text-gray-600">{label}</div>
							<div className="text-xl font-bold">{value}</div>
						</Box>
					</Grid>
				))}
			</Grid>
		</Stack>
	);
};

const CompanyToolBar = (props: any) => (
	<Toolbar>
		<SaveButton />
	</Toolbar>
);
const CompanyInfoText = (record: any) => {
	return (
		<Stack>
			<RichTextInput source="subscription_info.he" label="מה מגיע לי (עברית)" />
			<RichTextInput source="subscription_info.en" label="מה מגיע לי (אנגלית)" textAlign={'right'} />
		</Stack>
	);
};

const CompanyEmployees = ({ refetch }: { refetch: () => void }) => {
	const [userToDelete, setUserToDelete] = useState<null | { email: string; id: string }>(null);
	const [verification, setVerification] = useState('');

	const handleDeleteConfirm = useCallback(
		async (id: string, email: string, verification: string) => {
			try {
				await axios.delete(`${constants.API_URL}/member/${id}`);
				setUserToDelete(null);
				refetch();
			} catch (error) {
				console.error(error);
			}
		},
		[refetch]
	);
	return (
		<>
			<Dialog open={userToDelete !== null} onClose={() => setUserToDelete(null)}>
				<DialogTitle>מחיקת משתמש</DialogTitle>
				<DialogContent>
					<div className="px-auto text-center flex flex-col gap-4">
						<div>
							<p className="text-lg">למחיקת המשתמש יש להקליד את כתובת המייל שלו</p>
							<p className="text-sm select-all">{userToDelete?.email}</p>
						</div>
						<p className="text-red-500 text-xs">
							אזהרה: פעולה זו תמחק את המשתמש לצמיתות ולא ניתן לשחזר אותה
						</p>

						<Input
							value={verification}
							onChange={(e) => setVerification(e.target.value)}
							className="px-8 text-center"
							inputProps={{ className: 'text-center' }}
							placeholder={userToDelete?.email}
						/>
						<Button
							disabled={verification !== userToDelete?.email}
							color="error"
							onClick={() => {
								if (userToDelete)
									handleDeleteConfirm(userToDelete?.id, userToDelete?.email, verification);
							}}
						>
							מחיקה
						</Button>
					</div>
				</DialogContent>
			</Dialog>
			<div>
				<h3>עובדי חברה</h3>
				<ReferenceManyField pagination={<MyPagination />} perPage={50} reference="member" target="company">
					<Datagrid bulkActionButtons={false} rowClick="show">
						<TextField source="email" />
						<TextField source="first_name" />
						<TextField source="last_name" />
						<FunctionField
							render={(record: any) => (
								<IconButton
									onClick={(e) => {
										setUserToDelete({ email: record.email, id: record._id });
										e.stopPropagation();
										return;
									}}
								>
									<BiTrash />
								</IconButton>
							)}
						/>
					</Datagrid>
				</ReferenceManyField>
			</div>
		</>
	);
};

const CompanyEdit = () => {
	const [addDomainModalVisible, setAddDomainModalVisible] = useState(false);

	const { permissions } = usePermissions();
	const [showDeleteDialog, setShowDeleteDialog] = useState(false);
	const handleDeleteCompanyButtonPress = () => {
		setShowDeleteDialog(true);
	};

	const { record, refetch } = useEditController();
	const notify = useNotify();

	const handleDeleteConfirm = async (confirm: string) => {
		try {
			const res = await axios.delete(`${constants.API_URL}/company/${record._id}/delete_users`, {
				data: { company_id: record._id, verification: confirm },
			});
			notify(`${res.data?.count || 'כל'} עובדי ${record?.english_name} נמחקו`);
			handleCloseDialog();
		} catch (error) {}
	};
	const handleCloseDialog = () => {
		setShowDeleteDialog(false);
	};

	const [showUdiFactorDialog, setShowUdiFactorDialog] = useState(false);
	const udiFactors = record?.udiFactors as Map<string, { value: number; updatedAt: Date; active: boolean }>;

	const handleAddDomain = useCallback(
		async (domain: string, verifierEmail: string) => {
			try {
				await axios.post(`${constants.API_URL}/company/${record._id}/domain`, { domain, verifierEmail });
				refetch();
			} catch (error) {
				notify(getLocalizedErrorMessage(error as AxiosError), { type: 'error' });
				throw error;
			}
		},
		[record?._id, refetch, notify]
	);
	const handleAddDomainEmailVerifier = useCallback(
		async (domain_id: string, verifierEmail: string) => {
			try {
				await axios.post(`${constants.API_URL}/company/${record._id}/domain/add-email`, {
					domain_id,
					verifierEmail,
				});
				refetch();
			} catch (error) {
				notify(getLocalizedErrorMessage(error as AxiosError), { type: 'error' });
				throw error;
			}
		},
		[record?._id, refetch, notify]
	);
	const handleResetSafeSpaceCount = async () => {
		try {
			const response = await axios.post(`${constants.API_URL}/company/${record._id}/reset_safe_space`);

			notify(`${response.data.updated ?? 0} משתמשים עודכנו`, { type: 'success' });
		} catch (error) {
			notify(getLocalizedErrorMessage(error as AxiosError), { type: 'error' });
		}
	};
	const [domainToEdit, setDomainToEdit] = useState<null | { _id: string; domain: string }>(null);

	const showUpdateVerifierModal = (domain_id: string, domain: string) => {
		setDomainToEdit({ _id: domain_id, domain });
		setAddDomainModalVisible(true);
	};
	const showAddDomainModal = () => {
		setDomainToEdit(null);
		setAddDomainModalVisible(true);
	};
	return (
		<>
			<AddDomainModal
				key={domainToEdit?._id}
				domain={domainToEdit}
				handleAddDomainEmailVerifier={handleAddDomainEmailVerifier}
				handleAdd={handleAddDomain}
				visible={addDomainModalVisible}
				onDismiss={() => {
					setAddDomainModalVisible(false);
				}}
			/>
			<FactorsModal
				onClose={() => {
					setShowUdiFactorDialog(false);
				}}
				open={showUdiFactorDialog}
				onFactorUpdate={async (name: string, value: number) => {
					try {
						await axios.patch(`${constants.API_URL}/company/${record._id}/udi_update_factor`, {
							name,
							value,
						});
						setShowUdiFactorDialog(false);
						notify('בוצע בהצלחה', { type: 'success' });
						refetch();
					} catch (error) {
						notify(getLocalizedErrorMessage(error as AxiosError), { type: 'error' });
					}
				}}
			/>
			<Edit mutationMode="pessimistic">
				<DeleteCompanyDialog
					companyName={record?.english_name}
					handleCloseDialog={() => {
						setShowDeleteDialog(false);
					}}
					isVisible={showDeleteDialog}
					handleDeleteConfirm={handleDeleteConfirm}
				/>
				<TabbedForm toolbar={<CompanyToolBar />}>
					<FormTab label={'מידע כללי'}>
						<GeneralCompanyInfo
							record={record}
							onAddUdiFactorsButtonPress={() => {
								setShowUdiFactorDialog(true);
							}}
							refetch={refetch}
							udiFactors={udiFactors}
						/>
					</FormTab>
					<FormTab label="באנר">
						<CompanyBannerTab />
					</FormTab>
					<FormTab label="הזדהות">
						<AuthOptions
							record={record}
							showUpdateVerifierModal={showUpdateVerifierModal}
							showAddDomainModal={showAddDomainModal}
							refetch={refetch}
						/>
					</FormTab>
					<FormTab label={'מה מגיע לי'}>
						<CompanyInfoText />
					</FormTab>

					<FormTab label={'היסטורית התחברות  '}>
						<LoginHistory />
					</FormTab>
					<FormTab label="HR">{record?._id ? <HREdit company_id={record?._id} /> : null}</FormTab>
					<FormTab label={'נתונים למתחם HR'}>
						<Grid container spacing={2}>
							<Grid item xs={3}>
								<NumberInput source="satisfaction" step={0.1} label="מדד שביעות רצון" fullWidth />
							</Grid>
							<Grid item xs={3}>
								<NumberInput source="perceived_value" step={0.1} label="הערך הנתפס" fullWidth />
							</Grid>
							<Box width="100%" />
							<Grid item xs={3}>
								<NumberInput fullWidth source="old_platform_subscribers" label="משתמשים באתר הישן" />
							</Grid>
							<Grid item xs={3}>
								<NumberInput fullWidth source="computed_usages" label="מס' שימושים משוקלל" />
							</Grid>
							<Grid item xs={12}>
								<BooleanInput
									label="הצג טוגל גרפים בתקציר מנהלים ללקוח"
									source="showExecutiveSummaryGraphs"
								/>
							</Grid>
							<Grid item xs={12}>
								{/* record is undefined on the very first render when the page is opened directly by URL */}
								{record?._id ? <ExecutiveSummaryGenerator companyId={record._id} /> : null}
							</Grid>
							<Grid item xs={12}>
								<RichTextInput
									fullWidth
									source="executiveSummary"
									label="תקציר מנהלים (מוצג ללקוח)"
									toolbar={
										<RichTextInputToolbar>
											<LevelSelect size={'medium'} />
											<FormatButtons size={'medium'} />
											<AlignmentButtons size="medium" />
											<ColorButtons size={'medium'} />
											<ListButtons size={'medium'} />
											<LinkButtons size={'medium'} />
											<ImageButtons size={'medium'} />
											<QuoteButtons size={'medium'} />
											<ClearButtons size={'medium'} />
										</RichTextInputToolbar>
									}
								/>
							</Grid>
						</Grid>
					</FormTab>

					<FormTab label={'מחלקות'}>
						<Departments record={record} />
					</FormTab>

					<FormTab label={'מילואימניקים'}>
						<MiluimForm />
					</FormTab>
					{record?._id === WIZMANN_COMPANY_ID && (
						<FormTab label={'wizmann type'}>
							<WizmannTypeStats companyId={record._id} />
						</FormTab>
					)}
					<FormTab label={'סטטיסטיקות'}>
						<Statistics />
					</FormTab>

					{['superadmin', 'business-admin', 'admin'].includes(permissions) && (
						<FormTab label={'ניהול תקציב'}>
							<SubsidyManagement />
						</FormTab>
					)}

					<FormTab label={'עובדים'}>
						<CompanyEmployees refetch={refetch} />
					</FormTab>
					{permissions === 'superadmin' && (
						<FormTab borderColor={'red'} label="סכנה">
							<div className="flex flex-col max-w-md gap-12 self-center">
								<h1>פעולות מסוכנות</h1>

								<div className="flex flex-col gap-2">
									<p className="text-sm">פעולה זו תאפס את כל הפגישות של החברה במרחב בטוח</p>
									<Button onClick={handleResetSafeSpaceCount} color="error" variant="outlined">
										איפוס פגישות מרחב בטוח
									</Button>
								</div>
								<div className="gap-2 flex flex-col">
									<p className="text-sm">
										שים לב, בלחיצה על מחיקת חברה, החברה תהפוך ללא פעילה, כל נתוני המשתמשים של אותה
										החברה יוצפנו ולא יוכלו יותר להכנס למערכת <br />
										<strong>פעולה זו בלתי הפיכה!</strong>
									</p>
									<Button onClick={handleDeleteCompanyButtonPress} color="error" variant="contained">
										מחיקת חברה
									</Button>
								</div>
							</div>
						</FormTab>
					)}
				</TabbedForm>
			</Edit>
		</>
	);
};

export default CompanyEdit;
